import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';

import routes from './app.routes';
import { provideServiceWorker } from '@angular/service-worker';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import { basicAuthInterceptor } from './http-interceptors/basic-auth.interceptor';
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { PublicClientApplication, InteractionType, LogLevel, BrowserCacheLocation, IPublicClientApplication } from '@azure/msal-browser';
import { environment } from '../environments/environment';
import { ENVIRONMENT } from '../environments/environment.token';
import { loadingProgressInterceptor } from './http-interceptors/loading-progress.interceptor';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateModule } from '@ngx-translate/core';

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      redirectUri: '/auth',
      postLogoutRedirectUri: '/logout',
    },
    cache: {
      // LocalStorage so accounts and refresh tokens survive mobile process kills
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      allowPlatformBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback: (level: LogLevel, message: string) => {
          if (level === LogLevel.Error) console.error('[MSAL]', message);
          else if (level === LogLevel.Warning) console.warn('[MSAL]', message);
        },
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: environment.loginRequest.scopes,
    },
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideAnimationsAsync(),
    provideHttpClient(
      withFetch(),
      withInterceptors([basicAuthInterceptor, loadingProgressInterceptor]),
    ),
    importProvidersFrom([
      TranslateModule.forRoot({
        loader: provideTranslateHttpLoader({
          prefix: './i18n/',
          suffix: '.json',
          useHttpBackend: true, // Bypasses HTTP interceptors
        }),
      }),
    ]),
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    provideAppInitializer(() => inject(MsalService).initialize()),
    { provide: ENVIRONMENT, useValue: environment },
  ],
};
